/**
 * System-wide cut-off for reconciliation *alerts* (health, badges, attention).
 *
 * Pre-cut-off orders/invoices stay searchable / manually reconcilable — they
 * must not contribute to attention counts or nags.
 *
 * Keep in sync with express_app/utils/reconciliationAlertCutoff.js
 */

/** Inclusive start of the alert window (UTC calendar day). */
export const RECONCILIATION_ALERT_CUTOFF_ISO = '2026-07-01'

const CUTOFF_MS = Date.parse(`${RECONCILIATION_ALERT_CUTOFF_ISO}T00:00:00.000Z`)

/**
 * @returns {Date} Start of 1 July 2026 UTC
 */
export function getReconciliationAlertCutoffDate() {
  return new Date(CUTOFF_MS)
}

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

/**
 * @returns {string} e.g. "1 Jul 2026"
 */
export function formatReconciliationAlertCutoff() {
  const [year, month, day] = RECONCILIATION_ALERT_CUTOFF_ISO.split('-')
  return `${Number(day)} ${MONTHS[Number(month) - 1]} ${year}`
}

/**
 * @param {unknown} value
 * @returns {Date|null}
 */
export function parseAlertDate(value) {
  if (value == null || value === '') {
    return null
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value
  }
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

/**
 * @param {unknown} value
 * @returns {boolean}
 */
export function isOnOrAfterReconciliationAlertCutoff(value) {
  const date = parseAlertDate(value)
  if (!date) {
    return false
  }
  return date.getTime() >= CUTOFF_MS
}

/**
 * Order-based issues use the order date.
 * @param {object} [order]
 * @returns {Date|null}
 */
export function getOrderAlertDate(order = {}) {
  return parseAlertDate(order?.orderedDate ?? order?.orderDate)
}

/**
 * Invoice-based issues: invoice date, else upload / created.
 * @param {object} [invoice]
 * @returns {Date|null}
 */
export function getInvoiceAlertDate(invoice = {}) {
  return (
    parseAlertDate(invoice?.invoiceDate) ||
    parseAlertDate(invoice?.uploadedDate) ||
    parseAlertDate(invoice?.createdAt)
  )
}

/**
 * @param {object} [order]
 * @returns {boolean}
 */
export function isOrderInReconciliationAlertWindow(order = {}) {
  const date = getOrderAlertDate(order)
  // Missing order date: keep alertable so we never hide an unknown current issue.
  if (!date) {
    return true
  }
  return isOnOrAfterReconciliationAlertCutoff(date)
}

/**
 * Invoice alertable when its own date is on/after the cut-off, or when any
 * linked order date is (boundary: old invoice + newer PO must still surface).
 *
 * @param {object} [invoice]
 * @param {{ relatedOrderDates?: unknown[] }} [options]
 * @returns {boolean}
 */
export function isInvoiceInReconciliationAlertWindow(
  invoice = {},
  { relatedOrderDates = [] } = {},
) {
  const related = Array.isArray(relatedOrderDates) ? relatedOrderDates : []
  if (related.some(isOnOrAfterReconciliationAlertCutoff)) {
    return true
  }

  const date = getInvoiceAlertDate(invoice)
  if (!date) {
    return true
  }
  return isOnOrAfterReconciliationAlertCutoff(date)
}

/**
 * @param {object[]} [orders]
 * @returns {object[]}
 */
export function filterOrdersInReconciliationAlertWindow(orders = []) {
  const list = Array.isArray(orders) ? orders : []
  return list.filter(isOrderInReconciliationAlertWindow)
}

/**
 * @param {object[]} [invoices]
 * @param {{ relatedOrderDatesByInvoiceId?: Record<string, unknown[]> }} [options]
 * @returns {object[]}
 */
export function filterInvoicesInReconciliationAlertWindow(
  invoices = [],
  { relatedOrderDatesByInvoiceId = {} } = {},
) {
  const list = Array.isArray(invoices) ? invoices : []
  const relatedMap = relatedOrderDatesByInvoiceId || {}

  return list.filter(invoice => {
    const id = String(invoice?._id || invoice?.id || '').trim()
    return isInvoiceInReconciliationAlertWindow(invoice, {
      relatedOrderDates: id ? relatedMap[id] || [] : [],
    })
  })
}

/**
 * Raise a request start date to the alert cut-off when it is earlier.
 * @param {string} startYYYYMMDD
 * @returns {string}
 */
export function clampDateRangeStartToAlertCutoff(startYYYYMMDD) {
  const start = String(startYYYYMMDD || '').trim()
  if (!start) {
    return RECONCILIATION_ALERT_CUTOFF_ISO
  }
  return start < RECONCILIATION_ALERT_CUTOFF_ISO
    ? RECONCILIATION_ALERT_CUTOFF_ISO
    : start
}

function normalisePoNumber(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
}

/**
 * Map invoices → related order dates so an old invoice linked to a post-cut-off
 * PO still stays alertable.
 *
 * @param {object[]} [invoices]
 * @param {object[]} [orders]
 * @returns {Record<string, Date[]>}
 */
export function buildRelatedOrderDatesByInvoiceId(invoices = [], orders = []) {
  const orderList = Array.isArray(orders) ? orders : []
  const ordersById = new Map()
  const ordersByPo = new Map()

  for (const order of orderList) {
    const date = getOrderAlertDate(order)
    if (!date) continue

    const orderId = String(order?._id || order?.id || '').trim()
    if (orderId) {
      const existing = ordersById.get(orderId) || []
      existing.push(date)
      ordersById.set(orderId, existing)
    }

    const po = normalisePoNumber(order?.purchaseOrderNumber)
    if (po) {
      const existing = ordersByPo.get(po) || []
      existing.push(date)
      ordersByPo.set(po, existing)
    }
  }

  const related = {}
  for (const invoice of Array.isArray(invoices) ? invoices : []) {
    const invoiceId = String(invoice?._id || invoice?.id || '').trim()
    if (!invoiceId) continue

    const dates = []
    const linkedId = String(invoice?.linkedOrderId || '').trim()
    if (linkedId && ordersById.has(linkedId)) {
      dates.push(...ordersById.get(linkedId))
    }

    for (const matchedId of invoice?.matchedOrderIds || []) {
      const id = String(matchedId || '').trim()
      if (id && ordersById.has(id)) {
        dates.push(...ordersById.get(id))
      }
    }

    const poNumbers = [
      invoice?.purchaseOrderNumber,
      ...(Array.isArray(invoice?.matchedPurchaseOrderNumbers)
        ? invoice.matchedPurchaseOrderNumbers
        : []),
    ]
    for (const poValue of poNumbers) {
      const po = normalisePoNumber(poValue)
      if (po && ordersByPo.has(po)) {
        dates.push(...ordersByPo.get(po))
      }
    }

    if (dates.length) {
      related[invoiceId] = dates
    }
  }

  return related
}
